// Credit approval from the Invoices page (client, 2026-11-05: "credit pe hai,
// Rukmani ji se nahi ho raha — sahi hai, but Admin / Super Admin ko kaise pata
// chalega?"). The same decision as Invoice Approvals → Credit approvals, right
// where the draft sits: Admin / Super Admin approve (Accounts can then post) or
// reject with a reason. The server checks the role again.
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { AlertTriangle, CheckCircle2, XCircle } from 'lucide-react';
import { api, getApiErrorMessage } from '@/shared/api/axios';
import { Modal } from '@/shared/components/ui/Modal';
import { Button } from '@/shared/components/ui/Button';
import { formatINR } from '@/shared/utils/currency';

export interface CreditApprovalInfo {
  status: 'pending' | 'approved' | 'rejected';
  message: string;
  reasons?: string[];
  limit?: number | null;
  outstanding?: number;
  overdueAmount?: number;
  overdueCount?: number;
  projected?: number;
  decidedByName?: string;
  note?: string;
}
export interface CreditApprovalTarget { requestId: string; number: string; clientName: string; total: number; credit: CreditApprovalInfo }

/** Pending credit approvals, for the Admin / Super Admin banner. */
export function usePendingCreditApprovals(enabled: boolean) {
  return useQuery({
    queryKey: ['invoice-requests', 'credit-approvals', 'pending'],
    queryFn: async () => (await api.get<{ items: Array<{ id: string; number: string; clientName: string; total: number; requestedByName?: string; creditApproval: CreditApprovalInfo }>; pending: number }>(
      '/api/invoice-requests/credit-approvals?status=pending',
    )).data,
    enabled,
    refetchInterval: 60_000,
  });
}

export function CreditApprovalDialog({ target, onClose }: { target: CreditApprovalTarget | null; onClose: () => void }) {
  const qc = useQueryClient();
  const [note, setNote] = useState('');
  const [rejecting, setRejecting] = useState(false);
  const decide = useMutation({
    mutationFn: async (decision: 'approve' | 'reject') => (await api.post(`/api/invoice-requests/${target!.requestId}/credit-approval`, { decision, note: note.trim() || undefined })).data,
    onSuccess: (_d, decision) => {
      toast.success(decision === 'approve' ? `Credit approved — ${target!.number} can now be posted.` : `Credit rejected for ${target!.number}.`);
      qc.invalidateQueries({ queryKey: ['invoices'] });
      qc.invalidateQueries({ queryKey: ['invoice-requests'] });
      qc.invalidateQueries({ queryKey: ['field'] });
      setNote(''); setRejecting(false);
      onClose();
    },
    onError: (e) => toast.error(getApiErrorMessage(e, 'Could not record the decision.')),
  });
  const c = target?.credit;
  return (
    <Modal open={Boolean(target)} onClose={() => { setRejecting(false); onClose(); }} size="md"
      title={target ? `Credit approval — ${target.number}` : ''}
      footer={target && c?.status === 'pending' ? (rejecting ? <>
        <Button variant="ghost" onClick={() => setRejecting(false)}>Back</Button>
        <Button variant="danger" disabled={!note.trim() || decide.isPending} onClick={() => decide.mutate('reject')}><XCircle className="h-4 w-4" /> Reject credit</Button>
      </> : <>
        <Button variant="ghost" onClick={() => setRejecting(true)}><XCircle className="h-4 w-4" /> Reject</Button>
        <Button disabled={decide.isPending} onClick={() => decide.mutate('approve')}><CheckCircle2 className="h-4 w-4" /> {decide.isPending ? 'Saving…' : 'Approve credit'}</Button>
      </>) : <Button variant="ghost" onClick={onClose}>Close</Button>}>
      {target && c && (
        <div className="space-y-3 text-sm">
          <p><b>{target.clientName}</b> — this bill {formatINR(target.total)}</p>
          <p className="flex items-start gap-1.5 rounded-lg bg-warning/10 px-3 py-2 text-xs font-semibold text-warning-text"><AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />{c.message}</p>
          <div className="grid grid-cols-2 gap-2 text-xs sm:grid-cols-4">
            <div><p className="text-on-surface-variant">Credit limit</p><p className="font-semibold">{c.limit ? formatINR(c.limit) : 'Not set'}</p></div>
            <div><p className="text-on-surface-variant">Outstanding</p><p className="font-semibold">{formatINR(c.outstanding || 0)}</p></div>
            <div><p className="text-on-surface-variant">Overdue</p><p className="font-semibold text-danger-text">{c.overdueAmount ? `${formatINR(c.overdueAmount)} · ${c.overdueCount}` : '—'}</p></div>
            <div><p className="text-on-surface-variant">After this bill</p><p className="font-semibold">{formatINR(c.projected || 0)}</p></div>
          </div>
          {c.status === 'pending' ? (
            <label className="block text-xs font-semibold text-on-surface-variant">{rejecting ? 'Reason for rejecting (the salesperson sees it) — required' : 'Note (optional), e.g. payment promised by 15th'}
              <input className="mt-1 h-9 w-full rounded border border-outline bg-surface px-2 text-sm" value={note} onChange={(e) => setNote(e.target.value)} maxLength={500} autoFocus={rejecting} />
            </label>
          ) : (
            <p className="text-xs">Credit {c.status} by {c.decidedByName || '—'}{c.note ? ` — ${c.note}` : ''}</p>
          )}
          <p className="text-[11px] text-on-surface-variant">Approving lets Accounts Finalize & Post this invoice. Admin / Super Admin posting it themselves also counts as approval.</p>
        </div>
      )}
    </Modal>
  );
}
